import { QueryClient } from '@tanstack/react-query';
import { FINANCIAL_TRANSACTION_API_KEY } from '@/features/dashboard/financialTransactions/api/financialTransactionsApi.queryKeys.ts';
import { PLANNED_TRANSACTIONS_BASE_KEY } from '@/features/dashboard/plannedTransactions/api/plannedTransactionsApi.queryKeys.ts';
import { CLIENT_BASE_KEY } from '@/features/dashboard/clients/api/clientsApi.queryKeys.ts';
import { PARTICIPANT_BASE_KEY } from '@/features/dashboard/participants/api/participantsApi.queryKeys.ts';
import { BALANCE_KEY_SEGMENT } from '@/features/dashboard/balances/api/balancesApi.queryKeys.ts';
import { EXPENSES_BASE_QUERY_KEY } from '@/features/dashboard/expenses/api/expensesApi.queryKeys.ts';
import { RAW_BANK_TRANSACTIONS_BASE_QUERY_KEY } from '@/features/dashboard/rawBankTransactions/api/rawBankTransactionsApi.queryKeys.ts';
import { TOUR_BUDGET_KEY_SEGMENT } from '@/features/dashboard/tourBudget/api/tourBudgetApi.queryKeys.ts';
import {
  DASHBOARD_FINANCE_KEY_SEGMENT,
  DASHBOARD_KEY_SEGMENT,
} from '@/features/dashboard/tours/api/tourDashboardApi.queryKeys.ts';
import { ATTENTION_KEY_SEGMENT } from '@/features/dashboard/tours/api/tourAttentionApi.queryKeys.ts';
import { BOOKINGS_BASE_KEY } from '@/features/dashboard/bookings/api/bookingsApi.queryKeys.ts';

const FINANCE_RELATED_KEY_SEGMENTS = [
  FINANCIAL_TRANSACTION_API_KEY,
  PLANNED_TRANSACTIONS_BASE_KEY,
  CLIENT_BASE_KEY,
  PARTICIPANT_BASE_KEY,
  BALANCE_KEY_SEGMENT,
  EXPENSES_BASE_QUERY_KEY,
  TOUR_BUDGET_KEY_SEGMENT,
  // Kasowanie transakcji z importu cofa przelew do `PENDING` po stronie backendu,
  // więc każda mutacja finansowa musi odświeżyć też wyciąg — inaczej picker
  // przelewów nie widzi zwolnionego przelewu aż do przeładowania strony.
  RAW_BANK_TRANSACTIONS_BASE_QUERY_KEY,
  // Sprawy wyjazdu liczą też pieniądze (zwroty, nadpłaty, faktury po terminie),
  // więc każda mutacja finansowa musi je odświeżyć razem z kartą finansów.
  ATTENTION_KEY_SEGMENT,
];

/**
 * Karta finansów każdego wyjazdu, nie tylko oglądanego. Mutacje transakcji
 * i wyciągu nie znają `tourId` — przelew potrafi dotknąć innego wyjazdu niż
 * ten na ekranie, więc unieważniamy prefiks, a nie jeden klucz.
 */
const isFinanceDashboardKey = (queryKey: readonly unknown[]): boolean =>
  queryKey.includes(DASHBOARD_KEY_SEGMENT) &&
  queryKey.includes(DASHBOARD_FINANCE_KEY_SEGMENT);

/**
 * Metryczka rezerwacji (saldo, raty), jej wpłaty i lista. Nie przez `includes`
 * jak reszta: pod `['bookings', id, …]` wiszą też rachunek, zgody, wiadomości
 * i wybory uczestników, których pieniądze nie ruszają. Tabela dokleja do klucza
 * stronę i filtry jako obiekty, więc tekstowy trzeci segment znaczy podzasób.
 */
const isBookingMoneyKey = (queryKey: readonly unknown[]): boolean =>
  queryKey[0] === BOOKINGS_BASE_KEY &&
  (typeof queryKey[2] !== 'string' || queryKey[2] === 'transactions');

export const isFinanceRelatedQueryKey = (
  queryKey: readonly unknown[],
): boolean =>
  isFinanceDashboardKey(queryKey) ||
  isBookingMoneyKey(queryKey) ||
  FINANCE_RELATED_KEY_SEGMENTS.some((segment) => queryKey.includes(segment));

export const invalidateFinanceRelatedQueries = (queryClient: QueryClient) => {
  queryClient.invalidateQueries({
    predicate: (query) => isFinanceRelatedQueryKey(query.queryKey),
    refetchType: 'active',
  });
};
